import { Stack, TextField } from '@vapor/material'
import { Autocomplete } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TagFragment, useTagsQuery } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import HicChipForTag from '../hic-chip/hic-chip-for-tag'

export type TagInputProps = {
  value?: string[]
  onChange?: (value: string[]) => void
  disabled?: boolean
  error?: boolean
  size?: 'small' | 'medium'
}

export default function TagInput({ value, onChange, disabled, error, size }: TagInputProps) {
  const tenant = useTenant()
  const { data } = useTagsQuery({ variables: { tenantId: tenant.id } })
  const options: TagFragment[] | undefined = useMemo(() => data?.tags || undefined, [data?.tags])
  const [localValue, setLocalValue] = useState<TagFragment[]>([])

  useEffect(() => {
    if (value && options) {
      const newLocalValue = options.filter((o) => value.includes(o.id))
      if (JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) {
        setLocalValue(newLocalValue)
      }
      const newValue = newLocalValue.map((t) => t.id)
      if (JSON.stringify(newValue) !== JSON.stringify(value)) {
        onChange?.(newValue)
      }
    }
  }, [value, options])

  const onLocalChange = useCallback(
    (newLocalValue: TagFragment[]) => {
      const newValue = newLocalValue.map((t) => t.id)
      if (JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) {
        setLocalValue(newLocalValue)
      }

      if (value && JSON.stringify(newValue) !== JSON.stringify(value)) {
        onChange?.(newValue)
      }
      if (!value && JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) {
        onChange?.(newValue)
      }
    },
    [value, onChange],
  )

  return (
    <Autocomplete //
      disabled={disabled}
      multiple
      options={options || []}
      renderInput={(params) => (
        <TextField //
          {...params}
          error={error}
          size={size}
        />
      )}
      renderTags={(value: TagFragment[] /* , getTagProps: AutocompleteRenderGetTagProps */) => {
        return (
          <Stack direction="row" gap={0.5} marginLeft={0.5}>
            {value.map((v) => {
              return <HicChipForTag label={v.label} color={v.color} key={v.label} />
            })}
          </Stack>
        )
      }}
      getOptionLabel={(option: TagFragment) => option.label}
      value={localValue}
      onChange={(event, value) => onLocalChange(value)}
    />
  )
}
